import React, { useMemo } from 'react';
import { sanitizeString } from '../utils/security';
import { formatChecklistCevapFromTamamlama } from '../utils/checklistDisplay';
import './AppointmentChecklistTab.css';

const safeText = (value) => sanitizeString(value ?? '');

const formatDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeSoruId = (id) => (id != null ? String(id) : '');

const sortBySira = (items) =>
  [...items].sort((a, b) => (Number(a.sira) || 0) - (Number(b.sira) || 0));

const AnswerBadge = ({ cevap, deger, degerBirimi }) => {
  const displayText = formatChecklistCevapFromTamamlama(cevap, deger, degerBirimi);
  if (cevap === 'evet') {
    return (
      <span className="kurulum-gereksinim-item__badge kurulum-gereksinim-item__badge--evet">
        {displayText}
      </span>
    );
  }
  if (cevap === 'hayir') {
    return (
      <span className="kurulum-gereksinim-item__badge kurulum-gereksinim-item__badge--hayir-danger">
        {displayText}
      </span>
    );
  }
  return (
    <span className="kurulum-gereksinim-item__badge kurulum-gereksinim-item__badge--bekliyor">
      Bekliyor
    </span>
  );
};

const AppointmentChecklistTab = ({ appointment }) => {
  const sablon = appointment?.checklistSablon;
  const tamamlama = appointment?.checklistTamamlama;
  const sk = appointment?.servisKaydi || {};
  const mhCihaz = appointment?.mhCihazOzeti;
  const techCihaz = appointment?.teknisyenCihazOnay;
  const telefonCevaplari = sk?.checklistTelefonCevaplari?.cevaplar || [];

  const mhUrun =
    mhCihaz?.urunAdi ||
    sk?.kurulumUrunAdi ||
    (Array.isArray(sk?.cihazlar) && sk.cihazlar[0]
      ? (sk.cihazlar[0].cihazAdi || sk.cihazlar[0].icerik?.icerikAdi || sk.cihazlar[0].urun?.urunAdi)
      : '') ||
    '';
  const mhSeri =
    mhCihaz?.seriNumarasi ||
    sk?.kurulumSeriNo ||
    (Array.isArray(sk?.cihazlar) && sk.cihazlar[0]?.seriNumarasi) ||
    '';
  const techUrun = techCihaz?.urunAdi || '';
  const techSeri = techCihaz?.seriNumarasi || '';
  const uyusmazlik = techCihaz?.mhIleUyusuyor === false;

  const checklistAd =
    tamamlama?.checklistAd || sablon?.ad || sablon?.checklistAd || 'Kontrol listesi';

  const cevapMap = useMemo(() => {
    const map = new Map();
    (tamamlama?.cevaplar || []).forEach((c) => {
      const key = normalizeSoruId(c.soruId);
      if (key) map.set(key, { cevap: c.cevap, deger: c.deger });
    });
    return map;
  }, [tamamlama?.cevaplar]);

  const sorular = useMemo(() => {
    const snapshot = tamamlama?.sorularSnapshot;
    if (Array.isArray(snapshot) && snapshot.length > 0) {
      return sortBySira(snapshot).map((s) => ({
        soruId: normalizeSoruId(s.soruId),
        metin: s.metin,
        sira: s.sira,
        degerBirimi: s.degerBirimi,
      }));
    }
    const tplSorular = sablon?.sorular;
    if (Array.isArray(tplSorular) && tplSorular.length > 0) {
      return sortBySira(tplSorular).map((s) => ({
        soruId: normalizeSoruId(s._id ?? s.soruId ?? s.id),
        metin: s.metin,
        sira: s.sira,
      }));
    }
    return [];
  }, [tamamlama?.sorularSnapshot, sablon?.sorular]);

  const tamamlanmaLabel = formatDateTime(tamamlama?.tamamlanmaTarihi);
  const hasAnyAnswer = sorular.some((s) => {
    const c = cevapMap.get(s.soruId);
    return c?.cevap === 'evet' || c?.cevap === 'hayir';
  });
  const showPendingEmpty = sorular.length > 0 && !hasAnyAnswer && !tamamlanmaLabel;

  if (!appointment) return null;

  return (
    <div className="appointment-checklist-tab">
      {(mhUrun || techUrun || mhSeri || techSeri) && (
        <div className="appointment-checklist-tab__header info-section" style={{ marginBottom: 16 }}>
          <h3 className="appointment-checklist-tab__title">Cihaz</h3>
          <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
            <div>
              <strong>Müşteri hizmetleri:</strong>{' '}
              {safeText(mhUrun || '—')}
              {mhSeri ? ` · Seri: ${safeText(mhSeri)}` : ''}
            </div>
            <div>
              <strong>Teknisyen:</strong>{' '}
              {safeText(techUrun || '—')}
              {techSeri ? ` · Seri: ${safeText(techSeri)}` : ''}
            </div>
            {uyusmazlik && (
              <div style={{ color: '#b45309', fontWeight: 600 }}>
                MH seçimi ile teknisyen onayı farklı
              </div>
            )}
          </div>
        </div>
      )}

      {telefonCevaplari.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div className="appointment-checklist-tab__header info-section">
            <h3 className="appointment-checklist-tab__title">Kontrol Listesi (Telefon)</h3>
          </div>
          <div className="kurulum-gereksinimlar__liste">
            {telefonCevaplari.map((g, index) => (
              <div key={`tel-${index}`} className="kurulum-gereksinim-item">
                <span className="kurulum-gereksinim-item__soru">{safeText(g.soruMetni)}</span>
                <AnswerBadge cevap={g.cevap} deger={g.deger} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="appointment-checklist-tab__header info-section">
        <h3 className="appointment-checklist-tab__title">Kontrol Listesi (Teknisyen) — {safeText(checklistAd)}</h3>
        {tamamlanmaLabel && (
          <div className="appointment-checklist-tab__meta">
            Tamamlanma: {tamamlanmaLabel}
          </div>
        )}
      </div>

      {showPendingEmpty && (
        <div className="appointment-checklist-tab__empty">
          Checklist henüz tamamlanmadı
        </div>
      )}

      {sorular.length > 0 ? (
        <div className="kurulum-gereksinimlar__liste">
          {sorular.map((soru, index) => {
            const cevapObj = cevapMap.get(soru.soruId);
            return (
            <div
              key={soru.soruId || `soru-${index}`}
              className="kurulum-gereksinim-item"
            >
              <span className="kurulum-gereksinim-item__soru">{safeText(soru.metin)}</span>
              <AnswerBadge
                cevap={cevapObj?.cevap}
                deger={cevapObj?.deger}
                degerBirimi={soru.degerBirimi}
              />
            </div>
            );
          })}
        </div>
      ) : (
        !showPendingEmpty && (
          <div className="appointment-checklist-tab__empty">
            Bu randevu için checklist sorusu bulunmuyor.
          </div>
        )
      )}
    </div>
  );
};

export default AppointmentChecklistTab;
